export type RateLimitOptions = {
  maxRequestsPerSecond?: number
  maxRetries?: number
  enableDeduplication?: boolean
  shouldRetry?: (status: number, retriesLeft: number) => boolean
  baseDelay?: number
  maxDelay?: number
}

/**
 * Creates a rate-limited fetch function with retry and optional deduplication.
 */
export function createRateLimitedFetch(opts: RateLimitOptions = {}): typeof fetch {
  const {
    maxRequestsPerSecond = 5,
    maxRetries = 3,
    enableDeduplication = true,
    shouldRetry = defaultShouldRetry,
    baseDelay = 100,
    maxDelay = 10_000,
  } = opts

  const requestTimes: number[] = []
  const pendingRequests = enableDeduplication ? new Map<string, Promise<Response>>() : null

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    await enforceRateLimit()

    // Handle deduplication for GET requests
    if (pendingRequests && (!init?.method || init.method.toUpperCase() === 'GET')) {
      const key = String(input)
      const existing = pendingRequests.get(key)
      if (existing) {
        return existing
      }

      const promise = executeWithRetry(input, init, maxRetries)
      pendingRequests.set(key, promise)
      promise.finally(() => pendingRequests.delete(key))
      return promise
    }

    return executeWithRetry(input, init, maxRetries)
  }

  async function enforceRateLimit(): Promise<void> {
    const now = Date.now()

    // Remove requests older than 1 second
    const cutoff = now - 1000
    while (requestTimes.length > 0 && requestTimes[0]! <= cutoff) {
      requestTimes.shift()
    }

    // Wait if at capacity
    if (requestTimes.length >= maxRequestsPerSecond) {
      const waitTime = requestTimes[0]! + 1000 - now + 1
      await sleep(waitTime)
      return enforceRateLimit()
    }

    requestTimes.push(now)
  }

  async function executeWithRetry(
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    retriesLeft: number,
  ): Promise<Response> {
    try {
      const response = await fetch(input, init)

      if (response.ok || !shouldRetry(response.status, retriesLeft)) {
        return response
      }

      if (retriesLeft > 0) {
        const delay = Math.min(baseDelay * (2 ** (maxRetries - retriesLeft)), maxDelay)
        await sleep(delay)
        return executeWithRetry(input, init, retriesLeft - 1)
      }

      return response
    } catch (error) {
      if (retriesLeft > 0) {
        const delay = Math.min(baseDelay * (2 ** (maxRetries - retriesLeft)), maxDelay)
        await sleep(delay)
        return executeWithRetry(input, init, retriesLeft - 1)
      }
      throw error
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, Math.max(0, ms)))
}

function defaultShouldRetry(status: number, retriesLeft: number): boolean {
  return retriesLeft > 0 && (status >= 500 || status === 429 || status === 408)
}
